import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2, Wallet, CreditCard, MapPinned } from "lucide-react";

export default function PaymentResult({ status }) {
  const [params] = useSearchParams();
  const [state, setState] = useState(status === "success" ? "loading" : "cancel");
  const [booking, setBooking] = useState(null);

  useEffect(() => {
    if (status !== "success") return;
    // Nuovo flusso: booking_id direttamente (in loco). Fallback su session_id (legacy).
    const id = params.get("booking_id") || params.get("session_id");
    if (!id) { setState("failed"); return; }
    (async () => {
      try {
        const { data } = await api.get(`/payments/status/${id}`);
        if (data.booking) { setState("paid"); setBooking(data.booking); }
        else { setState("failed"); }
      } catch { setState("failed"); }
    })();
  }, [status, params]);

  const pm = booking?.payment_method;
  const pmLabel = pm === "cash" ? "Contanti in loco" : pm === "card_on_site" ? "Carta in loco" : "In loco";
  const PmIcon = pm === "card_on_site" ? CreditCard : Wallet;

  return (
    <div className="max-w-lg mx-auto px-6 py-24 text-center">
      {state === "loading" && (
        <>
          <Loader2 className="w-14 h-14 mx-auto text-[#005C7A] animate-spin mb-6" />
          <h1 className="font-display text-3xl font-bold mb-2">Sto confermando…</h1>
        </>
      )}
      {state === "paid" && (
        <>
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="font-display text-4xl font-bold mb-3">Prenotazione confermata</h1>
          <p className="text-muted-foreground mb-6">
            Ti abbiamo mandato una email con il QR code. Mostralo alla reception all'arrivo.
          </p>
          {booking && (
            <div className="bg-white border border-border rounded-2xl p-5 text-left mb-6 space-y-3" data-testid="confirmation-details">
              <div>
                <div className="font-display text-lg font-semibold">{booking.pool_title}</div>
                <div className="text-sm text-muted-foreground">{booking.hotel_name} · {booking.date} · {booking.guests} ospite/i</div>
              </div>
              <div className="pt-3 border-t border-border flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-sm">
                  <div className="w-9 h-9 rounded-lg bg-[#005C7A]/10 flex items-center justify-center">
                    <PmIcon className="w-4 h-4 text-[#005C7A]" />
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Metodo scelto</div>
                    <div className="font-semibold text-sm">{pmLabel}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[11px] uppercase tracking-wider text-muted-foreground flex items-center gap-1 justify-end">
                    <MapPinned className="w-3 h-3" /> Da pagare in loco
                  </div>
                  <div className="font-display text-xl font-bold text-[#005C7A]">€{booking.amount?.toFixed(2)}</div>
                </div>
              </div>
              <div className="text-[11px] text-muted-foreground font-mono pt-1">
                ID transazione: <strong className="text-[#005C7A]" data-testid="success-short-id">{booking.short_id || booking.booking_id}</strong>
              </div>
            </div>
          )}
          <Link to="/guest"><Button className="rounded-full bg-[#005C7A]" data-testid="go-to-bookings">Le mie prenotazioni</Button></Link>
        </>
      )}
      {state === "cancel" && (
        <>
          <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-6">
            <XCircle className="w-10 h-10 text-muted-foreground" />
          </div>
          <h1 className="font-display text-3xl font-bold mb-3">Prenotazione annullata</h1>
          <Link to="/search"><Button className="rounded-full bg-[#005C7A]">Torna alla ricerca</Button></Link>
        </>
      )}
      {state === "failed" && (
        <>
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <XCircle className="w-10 h-10 text-red-600" />
          </div>
          <h1 className="font-display text-3xl font-bold mb-3">Qualcosa è andato storto</h1>
          <Link to="/search"><Button className="rounded-full bg-[#005C7A]">Torna alla ricerca</Button></Link>
        </>
      )}
    </div>
  );
}
